import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Mail, FileText, RefreshCw, ChevronRight, Users, Inbox, Send, ExternalLink, Database, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import HeaderBar from "../components/HeaderBar";
import FeedbackButton from "../components/FeedbackButton";
import ChatStatsCard from "../components/ChatStatsCard";
import ClientErrorsCard from "../components/ClientErrorsCard";
import FeatureFlagsCard from "../components/FeatureFlagsCard";
import ToolUsageCard from "../components/ToolUsageCard";
import BackfillThumbnailsCard from "../components/BackfillThumbnailsCard";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  listAdminUsers,
  listAdminFeedback,
  updateFeedbackStatus,
  sendDigestNow,
  getStripeDashboardLink,
  getResendDashboardLink,
  getMongoDashboardLink,
  getServicesStatus,
} from "../lib/api";

// V2.0.62f — Quick-status dot palette. Each service mode → semantic color.
// Live/atlas = healthy production-grade. Test/local = okay for dev. Mock/
// unknown = misconfigured.
const SERVICE_DOT_COLOR = {
  live: "bg-emerald-400",
  atlas: "bg-emerald-400",
  test: "bg-amber-400",
  local: "bg-amber-400",
  mock: "bg-amber-400",
  unknown: "bg-rose-400",
};

const ServiceBadge = ({ mode }) => {
  if (!mode) return <span className="ml-auto w-2 h-2 rounded-full bg-zinc-700" aria-label="loading" />;
  const dot = SERVICE_DOT_COLOR[mode] || "bg-zinc-500";
  return (
    <span className="ml-auto flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${dot}`} />
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
        {mode}
      </span>
    </span>
  );
};

/**
 * V1.9.0 — Owner-only admin console.
 *
 * Two tabs:
 *   - Users (default)   : beta-user list with last_active + credits + design/feedback counts.
 *   - Feedback inbox    : all feedback items with filter, status pills, expand-to-view + status update.
 *
 * Gated server-side by ADMIN_EMAILS — a non-admin user hitting /admin
 * will get a 403 from listAdminUsers and we show a friendly "Access
 * denied" pane instead of a stack trace.
 */
const STATUS_COLORS = {
  open:      "border-orange-500 bg-orange-950/40 text-orange-300",
  triaged:   "border-amber-500 bg-amber-950/40 text-amber-300",
  resolved:  "border-emerald-500 bg-emerald-950/40 text-emerald-300",
  wontfix:   "border-zinc-500 bg-zinc-900 text-zinc-400",
};

const SEVERITY_COLORS = {
  blocker: "bg-orange-600 text-white",
  high:    "bg-amber-500 text-zinc-950",
  normal:  "bg-brass-600 text-zinc-950",
  low:     "bg-zinc-700 text-zinc-300",
};

const formatRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
};

export default function AdminConsole() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const [u, f] = await Promise.all([
        listAdminUsers(),
        listAdminFeedback(statusFilter || undefined),
      ]);
      setUsers(u.data?.users || []);
      setFeedback(f.data?.feedback || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, statusFilter]);

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      await updateFeedbackStatus(feedbackId, newStatus);
      setFeedback((prev) =>
        prev.map((f) =>
          f.feedback_id === feedbackId ? { ...f, status: newStatus } : f,
        ),
      );
      toast.success(`Marked ${newStatus}`);
    } catch {
      toast.error("Couldn't update status");
    }
  };

  const [sendingDigest, setSendingDigest] = useState(false);
  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const r = await sendDigestNow();
      const d = r.data;
      if (d?.sent) {
        toast.success(
          `Digest sent — ${d.summary?.feedback ?? 0} feedback, ${d.summary?.new_users ?? 0} new users`,
        );
      } else {
        toast(`Nothing to send today (${d?.reason || "empty"})`);
      }
    } catch {
      toast.error("Failed to send digest — is FEEDBACK_DIGEST_EMAIL configured?");
    } finally {
      setSendingDigest(false);
    }
  };

  // V2.0.62c–e — Services dropdown opens external dashboards in the
  // right mode based on the live env keys. One generic handler covers
  // Stripe / Resend / Mongo with consistent toast messaging.
  const [openingService, setOpeningService] = useState(null); // 'stripe' | 'resend' | 'mongo' | null
  const openService = async (key, fetchLink, labels) => {
    setOpeningService(key);
    try {
      const r = await fetchLink();
      const { url, mode } = r.data;
      if (labels.unavailable && labels.unavailable.includes(mode)) {
        toast(labels.unavailableMsg || `${labels.title} not configured.`);
        return;
      }
      toast(labels.openMsg ? labels.openMsg(mode) : `Opening ${labels.title}`);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.debug(`[AdminConsole] ${key} dashboard link failed:`, err);
      toast.error(`Couldn't open ${labels.title}.`);
    } finally {
      setOpeningService(null);
    }
  };

  const handleOpenStripeDashboard = () =>
    openService("stripe", getStripeDashboardLink, {
      title: "Stripe Dashboard",
      openMsg: (mode) =>
        mode === "unknown"
          ? "Stripe key not detected — opening dashboard root."
          : `Opening Stripe Dashboard (${mode} mode)`,
    });

  const handleOpenResendDashboard = () =>
    openService("resend", getResendDashboardLink, {
      title: "Resend logs",
      openMsg: (mode) =>
        mode === "mock"
          ? "Resend key is mocked — opening dashboard anyway."
          : "Opening Resend delivery logs",
    });

  const handleOpenMongoDashboard = () =>
    openService("mongo", getMongoDashboardLink, {
      title: "MongoDB Atlas",
      unavailable: ["local"],
      unavailableMsg:
        "MongoDB is running locally — no Atlas dashboard for this env.",
      openMsg: () => "Opening MongoDB Atlas",
    });

  // V2.0.62f — Quick-status badges. Fetch once when the dropdown opens so
  // the dots reflect the current env without a wasted call on first mount.
  const [servicesStatus, setServicesStatus] = useState(null);
  const handleServicesOpenChange = async (open) => {
    if (!open || servicesStatus) return;
    try {
      const r = await getServicesStatus();
      setServicesStatus(r.data);
    } catch (err) {
      console.debug("[AdminConsole] services status fetch failed:", err);
      // Render the dropdown anyway — badges just show as 'loading' dots.
    }
  };

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100">
        <HeaderBar />
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="font-mono text-[10px] uppercase tracking-widest text-orange-400 mb-2">
            // ADMIN · 403
          </div>
          <h1 className="font-display text-3xl mb-3">Access denied</h1>
          <p className="text-zinc-400 mb-6">
            Your email isn't on the admin list. If you should have access, ask the owner to add you
            to the <code className="text-brass-400">ADMIN_EMAILS</code> environment variable.
          </p>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="px-5 py-2.5 bg-brass-600 hover:bg-brass-500 text-zinc-950 font-mono text-[10px] uppercase tracking-widest"
          >
            Back to projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="admin-console">
      <HeaderBar />
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">
              // ADMIN · BETA OPS
            </div>
            <h1 className="font-display text-3xl text-zinc-50 mt-1">Beta console</h1>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu onOpenChange={handleServicesOpenChange}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="admin-services-menu"
                  title="External service dashboards (Stripe, Resend, Mongo)"
                  disabled={!!openingService}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-brass-700 hover:border-brass-500 text-brass-300 hover:text-brass-100 font-mono text-[10px] uppercase tracking-widest disabled:opacity-50"
                >
                  {openingService ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <ExternalLink className="w-3 h-3" />
                  )}
                  Services
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-zinc-950 border border-zinc-800 text-zinc-100 min-w-[16rem]"
              >
                <DropdownMenuLabel className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                  External dashboards
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem
                  data-testid="admin-services-stripe"
                  onSelect={handleOpenStripeDashboard}
                  className="cursor-pointer focus:bg-zinc-900 focus:text-brass-100"
                >
                  <ExternalLink className="w-3.5 h-3.5 text-brass-400" />
                  <span className="text-xs">Stripe Dashboard</span>
                  <ServiceBadge mode={servicesStatus?.stripe?.mode} />
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="admin-services-resend"
                  onSelect={handleOpenResendDashboard}
                  className="cursor-pointer focus:bg-zinc-900 focus:text-brass-100"
                >
                  <Mail className="w-3.5 h-3.5 text-brass-400" />
                  <span className="text-xs">Resend logs</span>
                  <ServiceBadge mode={servicesStatus?.resend?.mode} />
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="admin-services-mongo"
                  onSelect={handleOpenMongoDashboard}
                  className="cursor-pointer focus:bg-zinc-900 focus:text-brass-100"
                >
                  <Database className="w-3.5 h-3.5 text-brass-400" />
                  <span className="text-xs">MongoDB Atlas</span>
                  <ServiceBadge mode={servicesStatus?.mongo?.mode} />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              type="button"
              onClick={handleSendDigest}
              disabled={sendingDigest}
              data-testid="admin-send-digest-now"
              title="Send the daily feedback digest now"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-amber-700 hover:border-amber-500 text-amber-300 hover:text-amber-100 font-mono text-[10px] uppercase tracking-widest disabled:opacity-50"
            >
              {sendingDigest ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Send className="w-3 h-3" />
              )}
              Send digest now
            </button>
            <button
              type="button"
              onClick={fetchAll}
              disabled={loading}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 font-mono text-[10px] uppercase tracking-widest disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              Refresh
            </button>
          </div>
        </div>

        {/* V2.0.65 Chat latency dashboard */}
        <ChatStatsCard />

        {/* V2.0.73 Client-error viewer */}
        <ClientErrorsCard />

        {/* V2.0.72 Admin feature flags */}
        <FeatureFlagsCard />

        {/* V2.0.81 Tool-usage heatmap */}
        <ToolUsageCard />

        {/* V2.0.88 Backfill legacy thumbnails */}
        <BackfillThumbnailsCard />

        {/* Tab switcher */}
        <div className="inline-flex border border-zinc-700 mb-6">
          <button
            type="button"
            onClick={() => setTab("users")}
            data-testid="admin-tab-users"
            aria-pressed={tab === "users"}
            className={
              "inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors " +
              (tab === "users"
                ? "bg-brass-600 text-zinc-950"
                : "text-zinc-400 hover:text-zinc-100")
            }
          >
            <Users className="w-3 h-3" /> Users ({users.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("feedback")}
            data-testid="admin-tab-feedback"
            aria-pressed={tab === "feedback"}
            className={
              "inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border-l border-zinc-700 transition-colors " +
              (tab === "feedback"
                ? "bg-brass-600 text-zinc-950"
                : "text-zinc-400 hover:text-zinc-100")
            }
          >
            <Inbox className="w-3 h-3" /> Feedback ({feedback.length})
          </button>
        </div>

        {loading && (
          <div className="text-zinc-500 font-mono text-xs">Loading…</div>
        )}

        {!loading && tab === "users" && (
          <div className="border border-zinc-800" data-testid="admin-users-table">
            <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-zinc-900 border-b border-zinc-800 font-mono text-[9px] uppercase tracking-widest text-zinc-500">
              <div className="col-span-3">User</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-2">Last active</div>
              <div className="col-span-1 text-right">Designs</div>
              <div className="col-span-1 text-right">Feedback</div>
              <div className="col-span-1 text-right">Credits</div>
              <div className="col-span-1 text-right">Joined</div>
            </div>
            {users.length === 0 ? (
              <div className="px-4 py-8 text-zinc-500 text-center font-mono text-xs">
                No users yet.
              </div>
            ) : (
              users.map((u) => (
                <div
                  key={u.user_id}
                  data-testid={`admin-user-row-${u.user_id}`}
                  className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-zinc-800 last:border-b-0 items-center hover:bg-zinc-900/50"
                >
                  <div className="col-span-3 flex items-center gap-2 min-w-0">
                    {u.picture ? (
                      <img
                        src={u.picture}
                        alt={u.name}
                        className="w-6 h-6 border border-zinc-700 object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-6 h-6 border border-zinc-700 bg-zinc-800 grid place-items-center text-[9px] font-mono flex-shrink-0">
                        {u.name?.[0] || "?"}
                      </div>
                    )}
                    <span className="text-zinc-100 text-sm truncate">{u.name || "—"}</span>
                  </div>
                  <div className="col-span-3 text-zinc-400 text-xs font-mono truncate">{u.email}</div>
                  <div className="col-span-2 text-zinc-400 text-xs font-mono">{formatRelative(u.last_active_at)}</div>
                  <div className="col-span-1 text-right text-zinc-300 font-mono text-sm">{u.design_count}</div>
                  <div className="col-span-1 text-right text-zinc-300 font-mono text-sm">{u.feedback_count}</div>
                  <div className="col-span-1 text-right text-brass-400 font-mono text-sm">{u.professional_credits ?? 0}</div>
                  <div className="col-span-1 text-right text-zinc-500 font-mono text-[10px]">{formatRelative(u.created_at)}</div>
                </div>
              ))
            )}
          </div>
        )}

        {!loading && tab === "feedback" && (
          <div className="space-y-3" data-testid="admin-feedback-list">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                Filter:
              </span>
              {["", "open", "triaged", "resolved", "wontfix"].map((s) => (
                <button
                  key={s || "all"}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  data-testid={`admin-filter-${s || "all"}`}
                  aria-pressed={statusFilter === s}
                  className={
                    "px-2 py-1 border font-mono text-[10px] uppercase tracking-widest transition-colors " +
                    (statusFilter === s
                      ? "border-brass-500 bg-brass-950 text-brass-100"
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-500")
                  }
                >
                  {s || "all"}
                </button>
              ))}
            </div>
            {feedback.length === 0 ? (
              <div className="border border-zinc-800 px-4 py-8 text-zinc-500 text-center font-mono text-xs">
                No feedback {statusFilter ? `with status ${statusFilter}` : "yet"}.
              </div>
            ) : (
              feedback.map((f) => (
                <div
                  key={f.feedback_id}
                  data-testid={`admin-feedback-row-${f.feedback_id}`}
                  className="border border-zinc-800"
                >
                  <button
                    type="button"
                    onClick={() => setExpanded(expanded === f.feedback_id ? null : f.feedback_id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-zinc-900/50"
                  >
                    <span
                      className={
                        "px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest " +
                        (SEVERITY_COLORS[f.severity] || SEVERITY_COLORS.normal)
                      }
                    >
                      {f.severity}
                    </span>
                    <span
                      className={
                        "px-1.5 py-0.5 border font-mono text-[9px] uppercase tracking-widest " +
                        (STATUS_COLORS[f.status] || STATUS_COLORS.open)
                      }
                    >
                      {f.status}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      {f.category}
                    </span>
                    <span className="flex-1 text-zinc-100 text-sm truncate">{f.message}</span>
                    <span className="font-mono text-[10px] text-zinc-500">{formatRelative(f.created_at)}</span>
                    <ChevronRight
                      className={
                        "w-4 h-4 text-zinc-500 transition-transform " +
                        (expanded === f.feedback_id ? "rotate-90" : "")
                      }
                    />
                  </button>
                  {expanded === f.feedback_id && (
                    <div className="border-t border-zinc-800 px-4 py-3 bg-zinc-900/30 space-y-3">
                      <div className="text-zinc-200 text-sm whitespace-pre-wrap">
                        {f.message}
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-xs font-mono">
                        <div>
                          <div className="text-zinc-500 uppercase tracking-widest text-[9px]">From</div>
                          <div className="text-zinc-300 flex items-center gap-1">
                            <Mail className="w-3 h-3 text-zinc-500" /> {f.user_email}
                          </div>
                        </div>
                        <div>
                          <div className="text-zinc-500 uppercase tracking-widest text-[9px]">Design</div>
                          <div className="text-zinc-300">
                            {f.design_id ? (
                              <a href={`/designs/${f.design_id}`} className="text-brass-400 hover:text-brass-300 flex items-center gap-1">
                                <FileText className="w-3 h-3" /> {f.design_id}
                              </a>
                            ) : (
                              <span className="text-zinc-500">— none —</span>
                            )}
                          </div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-zinc-500 uppercase tracking-widest text-[9px]">URL</div>
                          <a href={f.page_url} className="text-brass-400 hover:text-brass-300 truncate block" target="_blank" rel="noreferrer">
                            {f.page_url}
                          </a>
                        </div>
                        <div className="col-span-2">
                          <div className="text-zinc-500 uppercase tracking-widest text-[9px]">User agent · viewport</div>
                          <div className="text-zinc-400 text-[11px]">{f.user_agent} · {f.viewport}</div>
                        </div>
                        {f.screenshot_data_url && (
                          <div
                            className="col-span-2"
                            data-testid={`admin-fb-${f.feedback_id}-screenshot`}
                          >
                            <div className="text-zinc-500 uppercase tracking-widest text-[9px] mb-1">Screenshot</div>
                            <a
                              href={f.screenshot_data_url}
                              target="_blank"
                              rel="noreferrer"
                              className="block border border-zinc-700 hover:border-brass-500"
                            >
                              <img
                                src={f.screenshot_data_url}
                                alt="User-submitted screenshot"
                                className="w-full max-h-72 object-contain bg-zinc-950"
                              />
                            </a>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                          Mark as:
                        </span>
                        {["open", "triaged", "resolved", "wontfix"].map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => handleStatusChange(f.feedback_id, s)}
                            data-testid={`admin-fb-${f.feedback_id}-set-${s}`}
                            disabled={f.status === s}
                            className={
                              "px-2 py-1 border font-mono text-[10px] uppercase tracking-widest transition-colors " +
                              (f.status === s
                                ? "border-brass-500 bg-brass-950 text-brass-100 cursor-default"
                                : "border-zinc-700 text-zinc-400 hover:border-zinc-500")
                            }
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
      <FeedbackButton />
    </div>
  );
}
